import { AnalyticIdentifier } from '@common/neo.tax';
import { Stack, ControlledInput } from '@ui-components/neo.tax';
import firebase from 'firebase/compat/app';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useAuthErrorMessage } from '@neo.tax/onboarding/hooks';
import { StyledGeneralFormError, SubmitButton } from '@neo.tax/onboarding/organisms';
import { validatePassword, useAnalyticId, useNavigateTo, getPath, Path } from '@neo.tax/shared';

import { ResetPasswordFormValues } from './reset-password.types';

export const ResetPasswordForm = ({ oobCode }: { oobCode: string }): React.ReactElement => {
  const navigate = useNavigateTo();
  const { control, formState, handleSubmit, watch } = useForm<ResetPasswordFormValues>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useAuthErrorMessage();
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  const passwordInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_PASSWORD_INPUT);
  const confirmPasswordInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_PASSWORD_CONFIRMATION_INPUT);

  const [password, confirmPassword] = watch(['password', 'confirmPassword']);

  useEffect(() => {
    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords don't match");
    } else {
      setConfirmPasswordError(null);
    }
  }, [password, confirmPassword]);

  const onSubmit = async (): Promise<void> => {
    setIsSubmitted(true);
    try {
      await firebase.auth().verifyPasswordResetCode(oobCode);
      await firebase.auth().confirmPasswordReset(oobCode, password);

      navigate(getPath({ path: Path.LOGIN }));
    } catch {
      setErrorMessage('Failed to reset password. Invalid or expired password reset code');
      setIsSubmitted(false);
    }
  };

  return (
    <Stack as="form" direction="column" paddingY={6} onSubmit={handleSubmit(onSubmit)}>
      <ControlledInput
        control={control}
        name="password"
        rules={{ required: true, validate: validatePassword }}
        id={passwordInputId}
        label="New Password"
        type="password"
        placeholder="Enter new password"
      />

      <ControlledInput
        control={control}
        name="confirmPassword"
        rules={{ required: true }}
        id={confirmPasswordInputId}
        label="Confirm New Password"
        type="password"
        errorText={confirmPasswordError || undefined}
        placeholder="Confirm new password"
      />

      <SubmitButton
        disabled={!!confirmPasswordError || !formState.isValid || isSubmitted}
        variant="primary"
        rounded="regular"
        size="base"
        type="submit"
      >
        Reset Password
      </SubmitButton>

      <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
    </Stack>
  );
};
